/**
 * Crucible #1945 — translate the cloud runtime's `dev.start@1`
 * streaming chunks into the cookie-mode preview contract the shared
 * Preview overlay consumes: the `CloudPreviewDevSession` returned by
 * `invoke('dev:start')` and the session-scoped `preview:cloud-update`
 * push events.
 *
 * `BrowserCrucibleClient.invoke('dev:start', …)` routes the launch over
 * `invokeStreaming` (the chunks are `dev.start.chunk@1` notifications
 * correlated by `params.requestId`), then bridges the stream through
 * these helpers so `usePreviewSession.start()` resolves with the cloud
 * session shape it awaits — WITHOUT the call site changing. This is the
 * `dev:start` twin of the `game:create` (#1314) and `agent:send`
 * (#1777) bridges.
 *
 * Why a bridge is REQUIRED: `dev.start@1` is a streaming method, but in
 * cookie mode `dev:start` was neither runtime-bridged nor stubbed, so
 * `invoke('dev:start')` fell through to a BARE `sendRequest`. The
 * request-correlated `dev.start.chunk@1` chunks then had no registered
 * streaming handler (`stream chunk for unknown requestId — dropping`)
 * and the bare request never resolved → the preview overlay hung on its
 * placeholder forever (the #1932 furnace-preview spinner).
 *
 * The consume sequence mirrors the desktop orchestrator
 * (`crucible-gui/src/main/cloud-preview-launch.ts`), minus the
 * main-process concerns the browser doesn't have (pre-flight auth — the
 * browser already carries the FA cookie — the session registry, and
 * dev-status persistence):
 *
 *   FIRST `preview-url{state:ready|restart, externalUrl}` chunk
 *     → adopt: resolve a `{runtime:'cloud', sessionId, gameId, previewUrl}`
 *   later differing externalUrl → push `url-swap`
 *   terminal (stream done / error / end) → push `closed`
 *
 * **Version skew is loud (TDD D4 / crucible#1478):** a `preview-url`
 * chunk WITHOUT `externalUrl` means the pod image predates the
 * externalUrl wire field or `FOUNDRY_PREVIEW_BASE_URL` is missing on the
 * pod (bifrost#122). The pod-local `url` is unreachable from the browser,
 * so adopting it would iframe an unreachable host and present as the same
 * infinite spinner — the launcher throws instead.
 */

import type {
  CloudPreviewDevSession,
  CloudPreviewPushEvent,
  DevSessionPairingMetadata,
} from '@volley/crucible-ipc-types';

/**
 * Budget for the `dev:start` bridge to receive the first ready
 * `preview-url` chunk before it rejects. Mirrors the desktop's
 * `FIRST_URL_TIMEOUT_MS` (`cloud-preview-launch.ts`): a cold pod
 * (`pnpm install`) can take minutes, so this is generous — but BOUNDED,
 * so a stalled / log-only stream rejects rather than hanging the preview
 * overlay forever (Codex P2 on #1948). Overridable per-client via
 * `BrowserCrucibleClientOptions.devStartFirstUrlTimeoutMs` (tests inject a
 * short value).
 */
export const DEV_START_FIRST_URL_TIMEOUT_MS = 180_000;

/**
 * Mirror of the runtime's originator wire chunk for `dev.start@1` (the
 * `dev.start.chunk@1` notification `params`, minus the `requestId`/`seq`
 * envelope fields the core strips). Re-declared here because
 * `@volley/crucible-client` does not depend on `@volley/crucible` (the
 * runtime package that owns the chunk type); the streaming
 * notifications arrive as `params` objects of this shape.
 *
 * `chunkKind` ∈ `log` | `error` | `preview-url` | `done`. `externalUrl`
 * rides on `preview-url` chunks when the pod's Furnace controller
 * injected `FOUNDRY_PREVIEW_BASE_URL` (crucible#1478 / bifrost#122); the
 * pod-local `url` is unreachable from the browser.
 */
export interface DevStartWireChunk {
  chunkKind: string;
  line?: string;
  /** Structured marker on a paired-lane `error` chunk — set to
   *  `'paired-tunnel-exited'` when the pod's cloudflared died mid-session,
   *  so the bridge can drop the pairing metadata without matching line text. */
  errorKind?: string;
  url?: string;
  externalUrl?: string;
  state?: string;
  gameId?: string;
  /**
   * Weekend-app paired cloud preview (crucible#pairing). On a paired
   * `preview-url` chunk the pod sets `previewMode: 'paired-server-only'`
   * and carries the tunnel `pairing` coordinates (minus the
   * `active`/`previewMode` the full DevSessionPairingMetadata adds — see
   * {@link pairingFromChunk}). Absent on classic previews.
   */
  previewMode?: 'paired-server-only';
  pairing?: {
    tunnelUrl: string;
    publicTunnelOrigin: string;
    publicHost: string;
    controllerBaseUrl: string;
  };
}

/**
 * A `preview-url` chunk is a "ready" signal when its `state` is `ready`
 * or `restart` (the two states the driver emits a URL on). Other
 * chunkKinds (`log`, `error`, `done`) and other states are not URL
 * signals.
 */
export function isReadyPreviewUrlChunk(chunk: DevStartWireChunk): boolean {
  return (
    chunk.chunkKind === 'preview-url' && (chunk.state === 'ready' || chunk.state === 'restart')
  );
}

/**
 * The external preview URL carried by a ready `preview-url` chunk, or
 * `null` when the chunk isn't a ready URL signal OR carries no non-empty
 * `externalUrl` (the version-skew case — see the module docblock). Never
 * falls back to the pod-local `url`: it is unreachable from the browser.
 */
export function previewUrlFromChunk(chunk: DevStartWireChunk): string | null {
  if (!isReadyPreviewUrlChunk(chunk)) return null;
  return typeof chunk.externalUrl === 'string' && chunk.externalUrl.length > 0
    ? chunk.externalUrl
    : null;
}

/**
 * The paired `previewMode` a ready chunk advertises (only
 * `'paired-server-only'` is defined), or `undefined` for a classic
 * preview. Guards against an off-contract value reaching the session.
 */
export function previewModeFromChunk(chunk: DevStartWireChunk): 'paired-server-only' | undefined {
  return chunk.previewMode === 'paired-server-only' ? 'paired-server-only' : undefined;
}

/**
 * Reconstruct the full {@link DevSessionPairingMetadata} from a paired
 * ready chunk — the pod sends the four tunnel coordinates but omits the
 * `active`/`previewMode` the renderer's PairingPanel expects, so add
 * them here (mirrors the desktop bridge's `rendererPairingMetadata`).
 * Returns `undefined` unless the chunk is genuinely paired AND carries
 * all four string coordinates (a half-populated chunk degrades to no
 * pairing metadata rather than a malformed panel).
 */
export function pairingFromChunk(chunk: DevStartWireChunk): DevSessionPairingMetadata | undefined {
  if (chunk.previewMode !== 'paired-server-only') return undefined;
  const p = chunk.pairing;
  if (
    p === undefined ||
    typeof p.tunnelUrl !== 'string' ||
    typeof p.publicTunnelOrigin !== 'string' ||
    typeof p.publicHost !== 'string' ||
    typeof p.controllerBaseUrl !== 'string'
  ) {
    return undefined;
  }
  return {
    active: true,
    previewMode: 'paired-server-only',
    tunnelUrl: p.tunnelUrl,
    publicTunnelOrigin: p.publicTunnelOrigin,
    publicHost: p.publicHost,
    controllerBaseUrl: p.controllerBaseUrl,
  };
}

/** Build the cloud session shape `invoke('dev:start')` resolves with —
 *  the intentional cloud shape (`runtime:'cloud'`), NOT the local
 *  `DevSession`. The renderer narrows on `'runtime' in reply`. A paired
 *  cloud preview carries `previewMode` + `pairing` so the PairingPanel
 *  renders; classic omits both (byte-identical to the pre-pairing shape). */
export function makeCloudPreviewSession(
  gameId: string,
  sessionId: string,
  previewUrl: string,
  pairingMeta?: {
    previewMode?: 'paired-server-only';
    pairing?: DevSessionPairingMetadata;
  },
): CloudPreviewDevSession {
  return {
    runtime: 'cloud',
    sessionId,
    gameId,
    previewUrl,
    ...(pairingMeta?.previewMode !== undefined ? { previewMode: pairingMeta.previewMode } : {}),
    ...(pairingMeta?.pairing !== undefined ? { pairing: pairingMeta.pairing } : {}),
  };
}

/** A session-scoped `url-swap` push — the pod dev server restarted onto
 *  a new external URL. The renderer drops events whose `sessionId`
 *  differs from its adopted session. Carries the paired metadata so the
 *  PairingPanel survives the swap (classic omits it). */
export function devStartUrlSwapEvent(
  gameId: string,
  sessionId: string,
  previewUrl: string,
  pairingMeta?: {
    previewMode?: 'paired-server-only';
    pairing?: DevSessionPairingMetadata;
  },
): CloudPreviewPushEvent {
  return {
    kind: 'url-swap',
    gameId,
    sessionId,
    previewUrl,
    ...(pairingMeta?.previewMode !== undefined ? { previewMode: pairingMeta.previewMode } : {}),
    ...(pairingMeta?.pairing !== undefined ? { pairing: pairingMeta.pairing } : {}),
  };
}

/** A session-scoped `closed` push — the pod dev-server stream ended (or
 *  errored). The renderer drops the iframe back to the start
 *  affordance. */
export function devStartClosedEvent(
  gameId: string,
  sessionId: string,
  reason: 'ended' | 'error' = 'ended',
  message?: string,
): CloudPreviewPushEvent {
  return message === undefined
    ? { kind: 'closed', gameId, sessionId, reason }
    : { kind: 'closed', gameId, sessionId, reason, message };
}
